import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const SITE_URL = 'https://hoosierlifeinsurance.com';
const FORM_URL = 'https://readdy.ai/api/form/d74j8rn5hic0eqh316ig';

export default function CarmelLifeInsurance() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    coverage_amount: '', age: '', best_time: '', sms_consent: false,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Life Insurance in Carmel Indiana | Highest-Rated Independent Agency | Hoosier Life';

    const setMeta = (name: string, content: string, isProp = false) => {
      const attr = isProp ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.content = content;
    };

    setMeta('description', 'Life insurance in Carmel, Indiana — Indiana\'s highest-income city. Independent agency serving Clay Terrace, Village of West Clay, Carmel Arts District, Old Meridian & CNO Financial employees. Compare 20+ carriers. (317) 648-2983.');
    setMeta('keywords', 'life insurance Carmel Indiana, Carmel IN life insurance, Hamilton County life insurance, CNO Financial employee benefits, life insurance Village of West Clay, Carmel Indiana insurance agent');
    setMeta('geo.region', 'US-IN');
    setMeta('geo.placename', 'Carmel, Indiana');
    setMeta('geo.position', '39.9784;-86.1180');
    setMeta('ICBM', '39.9784, -86.1180');
    setMeta('og:title', 'Life Insurance in Carmel, Indiana | Hoosier Life Insurance', true);
    setMeta('og:description', 'Serving Carmel families in Clay Terrace, Village of West Clay, Carmel Arts District & more. Independent agency comparing 20+ carriers. Same-day quotes. (317) 648-2983.', true);
    setMeta('og:url', `${SITE_URL}/life-insurance-carmel`, true);
    setMeta('og:type', 'website', true);
    setMeta('twitter:card', 'summary_large_image');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${SITE_URL}/carmel-life-insurance`;

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${SITE_URL}/life-insurance-carmel#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much is life insurance for a 35-year-old in Carmel, Indiana?',
          acceptedAnswer: { '@type': 'Answer', text: 'A healthy 35-year-old in Carmel, Indiana pays approximately $28–$32 per month for $500,000 in 20-year term life coverage. Males pay around $32/month and females around $27/month at preferred health ratings. Indiana rates run 8–12% below the national average.' },
        },
        {
          '@type': 'Question',
          name: 'What does a $1 million life insurance policy cost in Carmel, Indiana?',
          acceptedAnswer: { '@type': 'Answer', text: 'A $1,000,000 20-year term life insurance policy for a healthy 35-year-old in Carmel costs approximately $45–$55 per month. For a 40-year-old Carmel resident, expect $65–$80/month. Given Carmel\'s average home value of $450,000+, most Carmel families choose $750K–$2M in coverage.' },
        },
        {
          '@type': 'Question',
          name: 'Do CNO Financial employees need personal life insurance beyond their group plan?',
          acceptedAnswer: { '@type': 'Answer', text: 'Yes. CNO Financial\'s group plan typically covers 1–2x salary — often $80,000–$200,000. Most Carmel families with mortgages above $400,000 and dual incomes need $1M–$2M in portable personal coverage. Group coverage ends the day you leave the company; personal coverage stays with you for life.' },
        },
        {
          '@type': 'Question',
          name: 'Is life insurance in Carmel more expensive than other Indiana cities?',
          acceptedAnswer: { '@type': 'Answer', text: 'No. Life insurance rates in Indiana are statewide and based on your age, health, coverage amount, and carrier — not your zip code. A Carmel resident pays the same rates as a Fishers or Indianapolis resident for identical coverage.' },
        },
        {
          '@type': 'Question',
          name: 'How quickly can a Carmel resident get life insurance coverage?',
          acceptedAnswer: { '@type': 'Answer', text: 'Many carriers offer same-day or next-day approval for healthy Carmel applicants. No-medical-exam simplified issue policies are active within 24–48 hours. Most Carmel families complete the entire process in a single 15–20 minute phone or Zoom call with a licensed agent.' },
        },
      ],
    };

    const speakableSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/life-insurance-carmel#speakable`,
      speakable: { '@type': 'SpeakableSpecification', xpath: ['/html/body//*[@id="carmel-price-summary"]'] },
      url: `${SITE_URL}/life-insurance-carmel`,
    };

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': ['InsuranceAgency', 'LocalBusiness'],
      '@id': `${SITE_URL}/carmel-life-insurance#localbusiness`,
      name: 'Hoosier Life Insurance — Carmel Indiana',
      url: `${SITE_URL}/carmel-life-insurance`,
      telephone: '+13176482983',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Carmel',
        addressRegion: 'IN',
        postalCode: '46032',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 39.9784,
        longitude: -86.1180,
      },
      areaServed: {
        '@type': 'City',
        name: 'Carmel',
        containedInPlace: { '@type': 'State', name: 'Indiana' },
      },
      description: 'Independent life insurance agency serving Carmel, Indiana. Clay Terrace, Village of West Clay, Carmel Arts District, Old Meridian Village, and CNO Financial employees.',
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '18:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '14:00' },
      ],
      priceRange: '$$',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '71',
        bestRating: '5',
        worstRating: '1',
      },
      sameAs: [
        'https://www.facebook.com/HoosierLifeInsurance',
        'https://twitter.com/HoosierLifeIns',
        'https://www.linkedin.com/company/hoosier-life-insurance',
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Life Insurance Products',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Term Life Insurance', description: '10, 20, and 30-year term policies from 20+ A-rated carriers' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Whole Life Insurance', description: 'Permanent whole life coverage with guaranteed cash value growth' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mortgage Protection Insurance', description: 'Decreasing term policy tied to mortgage balance for homeowners' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Final Expense Insurance', description: 'Simplified issue whole life covering burial and end-of-life costs' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'No Medical Exam Life Insurance', description: 'Same-day approval policies with no blood draw or doctor visit required' } },
        ],
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${SITE_URL}/carmel-life-insurance#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: `${SITE_URL}/service-areas` },
        { '@type': 'ListItem', position: 3, name: 'Life Insurance Carmel Indiana', item: `${SITE_URL}/carmel-life-insurance` },
      ],
    };

    const schemasToInject = [faqSchema, speakableSchema, localBusinessSchema, breadcrumbSchema];
    schemasToInject.forEach((schema, idx) => {
      const id = `schema-carmel-${idx}`;
      if (document.getElementById(id)) return;
      const el = document.createElement('script');
      el.id = id; el.type = 'application/ld+json'; el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    });

    return () => { schemasToInject.forEach((_, idx) => document.getElementById(`schema-carmel-${idx}`)?.remove()); };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, String(v)));
      await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      setFormSubmitted(true);
    } catch { setFormSubmitted(true); }
  };

  const faqs = [
    { q: 'How much is life insurance for a 35-year-old in Carmel, Indiana?', a: 'A healthy 35-year-old in Carmel pays approximately $28–$32/month for $500,000 in 20-year term coverage. Males pay around $32/month and females $27/month at preferred health ratings. Indiana rates run 8–12% below the national average — we shop 20+ carriers to find your lowest available rate.' },
    { q: 'What does a $1 million life insurance policy cost in Carmel?', a: 'A $1,000,000 20-year term policy for a healthy 35-year-old costs approximately $45–$55/month. For a 40-year-old, expect $65–$80/month. With Carmel\'s average home value above $450,000, most Carmel families choose between $750K and $2M in coverage to protect their mortgage and income.' },
    { q: 'Do CNO Financial employees need personal coverage beyond their group plan?', a: 'Absolutely. Group plans typically cover only 1–2× salary and disappear the day you leave the company. A Carmel family with a $450,000 mortgage and $120,000 household income needs $1.2M–$2M in coverage — their group plan provides maybe $160,000. The gap is enormous. A personal policy is portable and costs $40–$65/month for most CNO employees.' },
    { q: 'Is life insurance more expensive in Carmel than other Indiana cities?', a: 'No — Indiana life insurance rates are based on your age, health, and coverage amount, not your city. A Carmel resident with the same health profile as a Noblesville or Indianapolis resident pays the same rate. Your higher home value is a reason to buy more coverage, not a reason to pay more per dollar of coverage.' },
    { q: 'Can I get covered without a medical exam in Carmel?', a: 'Yes. Simplified issue policies give same-day or next-day approval with no blood draw, no doctor visit, and no waiting room. Coverage up to $2M is available through carriers like Bestow, Ladder, and Ethos. Most Carmel applicants are approved in under 15 minutes.' },
    { q: 'Does Hoosier Life Insurance serve all Carmel neighborhoods?', a: 'Yes — all consultations are by phone or Zoom. We serve Clay Terrace, Village of West Clay, Carmel Arts District, Old Meridian Village, Hunters Run, Gramercy, and all 46032, 46033, and 46074 zip codes in Carmel.' },
  ];

  return (
    <>
      <title>Life Insurance in Carmel Indiana | Highest-Rated Independent Agency | Hoosier Life</title>
      <meta name="description" content="Life insurance in Carmel, Indiana — independent agency serving West Clay, Arts District & all Carmel neighborhoods. Compare 20+ carriers. Free quotes. Call (317) 648-2983." />
      <link rel="canonical" href="https://hoosierlifeinsurance.com/carmel-life-insurance" />
      <meta name="robots" content="index, follow" />
      <meta property="og:title" content="Life Insurance in Carmel Indiana | Highest-Rated Independent Agency | Hoosier Life" />
      <meta property="og:url" content="https://hoosierlifeinsurance.com/carmel-life-insurance" />
      <meta name="geo.placename" content="Carmel, Indiana" />
      <meta name="geo.region" content="US-IN" />
      <meta name="geo.position" content="39.9784;-86.118" />
      <Header />

      {/* Hero */}
      <section className="relative bg-[#1A3A52] text-white pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="https://public.readdy.ai/ai/img_res/81bf3effba441dda3ac5056022298fad.jpg" alt="Aerial view of Carmel Indiana luxury residential neighborhood with upscale homes and tree-lined streets at golden hour" className="w-full h-full object-cover object-center" width={1400} height={700} loading="eager" fetchPriority="high" decoding="async" />
        </div>
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-8">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-white/50">
              <li><Link to="/" className="hover:text-[#D97642] transition-colors">Home</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-[#D97642] font-medium">Life Insurance Carmel IN</li>
            </ol>
          </nav>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 text-[#D97642] text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                <i className="ri-map-pin-2-fill"></i>
                Serving All Carmel ZIP Codes — 46032 · 46033 · 46074
              </div>
              <h1 className="text-4xl md:text-[46px] font-bold leading-tight mb-4">
                Carmel Life Insurance —<br />Independent Coverage, 20+ Carriers
              </h1>
              <p className="text-[17px] text-white/75 leading-relaxed mb-6">
                Indiana's highest-income city deserves its best protection. From Village of West Clay to the Carmel Arts District — independent coverage, 20+ carriers, same-day quotes.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="#carmel-quote-form" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#D97642] text-white font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-search-line text-lg"></i>Get My Free Rate
                </a>
                <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line text-lg"></i>(317) 648-2983
                </a>
              </div>
            </div>
            <div className="hidden lg:flex flex-col gap-3">
              {[
                { icon: 'ri-trophy-line', label: '#1 Safest City in Indiana', sub: '8 consecutive years — FBI Crime Report' },
                { icon: 'ri-home-4-line', label: '$450K+ Avg Home Value', sub: 'Your mortgage needs matching coverage' },
                { icon: 'ri-currency-line', label: '$109K+ Median Household Income', sub: 'Highest in Indiana — standard group plans fall short' },
                { icon: 'ri-shield-check-line', label: 'Same-Day Approval Available', sub: 'Most Carmel applicants approved in 15–30 min' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 bg-white/10 rounded-xl p-4 border border-white/10">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#D97642]/20 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#D97642] text-xl`}></i>
                  </div>
                  <div>
                    <p className="font-bold text-[15px]">{item.label}</p>
                    <p className="text-white/60 text-[12px]">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-[#D97642] text-white py-5">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { val: '$109K+', label: 'Median Income' },
            { val: '$450K+', label: 'Avg Home Value' },
            { val: '107K+', label: 'Residents' },
            { val: '20+', label: 'Carriers Compared' },
          ].map((s, i) => (
            <div key={i}><p className="text-[26px] font-black">{s.val}</p><p className="text-[13px] text-white/80 font-medium">{s.label}</p></div>
          ))}
        </div>
      </section>

      {/* Problem section */}
      <section className="py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[30px] md:text-[36px] font-bold text-[#1A3A52] mb-3">The Carmel Coverage Paradox</h2>
            <p className="text-[16px] text-[#718096] max-w-[560px] mx-auto">Carmel has Indiana's highest income — but high earners are chronically underinsured. Three reasons why.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: 'ri-building-2-line', title: 'Group Plans Are Woefully Inadequate', body: 'CNO Financial, Duke Energy, and ANGI group plans cover 1–2× salary. A $120K earner gets $120K–$240K. Your Carmel mortgage alone is likely $400K+. The math doesn\'t work.' },
              { icon: 'ri-home-line', title: 'Bigger Homes = Bigger Gaps', body: 'The average Carmel home is worth $450K+. Village of West Clay homes often exceed $700K. A mortgage that size requires $700K–$1.5M in personal coverage — far beyond any employer plan.' },
              { icon: 'ri-user-line', title: 'High Earners Wait Longest', body: 'Studies show high-income professionals are the most likely to delay buying life insurance. Every year you wait costs more. A 35-year-old pays $45/month for $1M; a 45-year-old pays $115/month for the same coverage.' },
            ].map((item, i) => (
              <div key={i} className="bg-[#F7F9FB] rounded-xl p-6 border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center bg-[#1A3A52]/10 rounded-xl mb-4">
                  <i className={`${item.icon} text-[#1A3A52] text-2xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A3A52] text-[16px] mb-2">{item.title}</h3>
                <p className="text-[14px] text-[#718096] leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing rate table */}
      <section id="carmel-price-summary" className="py-14 bg-[#F7F9FB] border-y border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-8">
            <h2 className="text-[28px] md:text-[34px] font-bold text-[#1A3A52] mb-2">What Does Life Insurance Cost in Carmel, Indiana?</h2>
            <p className="text-[15px] text-[#718096] max-w-[620px] mx-auto">Rates for healthy, non-smoking Carmel residents. Indiana rates run 8–12% below the national average.</p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white mb-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A3A52] text-white">
                  {['Age', 'Coverage', 'Term', 'Male/Month', 'Female/Month'].map(h => (
                    <th key={h} className="px-5 py-3.5 text-left font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { age: '30', cov: '$500,000', term: '20-year', m: '$28', f: '$23' },
                  { age: '35', cov: '$500,000', term: '20-year', m: '$32', f: '$27' },
                  { age: '35', cov: '$1,000,000', term: '20-year', m: '$51', f: '$43' },
                  { age: '40', cov: '$500,000', term: '20-year', m: '$45', f: '$38' },
                  { age: '40', cov: '$1,000,000', term: '20-year', m: '$79', f: '$66' },
                  { age: '45', cov: '$500,000', term: '20-year', m: '$68', f: '$56' },
                  { age: '50', cov: '$500,000', term: '20-year', m: '$115', f: '$92' },
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                    <td className="px-5 py-3 font-semibold text-[#1A3A52]">{row.age}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.cov}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.term}</td>
                    <td className="px-5 py-3 font-bold text-[#D97642]">{row.m}/mo</td>
                    <td className="px-5 py-3 font-bold text-[#D97642]">{row.f}/mo</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[12px] text-[#718096] italic">*Estimated rates for healthy non-smokers at Preferred Plus. Actual rates vary by carrier and health profile.</p>
            <a href="#carmel-quote-form" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97642] text-white text-[14px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-search-line"></i>Get My Exact Carmel Rate
            </a>
          </div>
        </div>
      </section>

      {/* Quote form + testimonials */}
      <section id="carmel-quote-form" className="py-16 md:py-24 bg-[#FDF6F0]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Form */}
            <div className="lg:col-span-3 bg-white rounded-2xl p-8 border border-gray-100">
              <h2 className="text-[26px] font-bold text-[#1A3A52] mb-2">Get Your Free Carmel Rate</h2>
              <p className="text-[14px] text-[#718096] mb-6">Compare 20+ carriers in 60 seconds. No pressure, no obligation.</p>
              {formSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-green-600 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-2">Got it — we'll be in touch shortly!</h3>
                  <p className="text-[#718096] text-sm">Benjamin will reach out within 1 business hour with your personalized Carmel rate comparison.</p>
                </div>
              ) : (
                <form id="carmel-quote-form-el" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <input type="hidden" name="source_page" value="Carmel Life Insurance Landing Page" />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">First Name *</label>
                      <input name="first_name" type="text" required value={formData.first_name} onChange={handleInputChange} placeholder="Emily" className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642]" />
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Last Name *</label>
                      <input name="last_name" type="text" required value={formData.last_name} onChange={handleInputChange} placeholder="Johnson" className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Email Address *</label>
                    <input name="email" type="email" required value={formData.email} onChange={handleInputChange} placeholder="emily@example.com" className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642]" />
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Phone Number *</label>
                    <input name="phone" type="tel" required value={formData.phone} onChange={handleInputChange} placeholder="(317) 555-0100" className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642]" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Your Age *</label>
                      <select name="age" required value={formData.age} onChange={handleInputChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer">
                        <option value="">Select</option>
                        {['18–25','26–30','31–35','36–40','41–45','46–50','51–55','56–60','61–65','65+'].map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Coverage Amount</label>
                      <select name="coverage_amount" value={formData.coverage_amount} onChange={handleInputChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer">
                        <option value="">Not sure</option>
                        {['$100K–$250K','$250K–$500K','$500K–$750K','$750K–$1M','$1M–$2M','$2M+'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Best Time to Call</label>
                    <select name="best_time" value={formData.best_time} onChange={handleInputChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer">
                      <option value="">Anytime</option>
                      {['Morning (8–11am)','Midday (11am–2pm)','Afternoon (2–5pm)','Evening (5–7pm)'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="flex items-start gap-3 bg-[#F7F9FB] rounded-lg p-3.5">
                    <input type="checkbox" name="sms_consent" id="sms-consent-carmel" checked={formData.sms_consent} onChange={handleInputChange} className="mt-0.5 cursor-pointer" />
                    <label htmlFor="sms-consent-carmel" className="text-[12px] text-[#718096] leading-relaxed cursor-pointer">
                      I agree to receive SMS/text messages from Hoosier Life Insurance. Message & data rates may apply. Reply STOP to opt out at any time.
                    </label>
                  </div>
                  <button type="submit" className="w-full py-3.5 bg-[#D97642] text-white font-bold rounded-xl hover:bg-[#C26638] transition-colors text-[16px] cursor-pointer whitespace-nowrap">
                    Get My Free Carmel Rate →
                  </button>
                  <p className="text-[11px] text-[#718096] text-center">No spam. No sales pressure. Cancel anytime.</p>
                </form>
              )}
            </div>

            {/* Testimonials */}
            <div className="lg:col-span-2 flex flex-col gap-5">
              <h3 className="text-[18px] font-bold text-[#1A3A52]">What Carmel Families Say</h3>
              {[
                { name: 'Michael & Sarah T.', area: 'Village of West Clay', stars: 5, text: 'Benjamin helped us get $1.5M in coverage for our West Clay home. We had no idea our CNO group plan was leaving us $1.3M short. The whole thing took one call.' },
                { name: 'Jennifer L.', area: 'Clay Terrace Area', stars: 5, text: 'Got approved for a $750K policy in 24 hours with no medical exam. As a busy mom in Carmel, that was exactly the speed I needed. Rates were way better than I expected.' },
                { name: 'David & Lisa R.', area: 'Carmel Arts District', stars: 5, text: 'We wanted whole life for generational wealth building. Benjamin walked us through an IUL that fits our income and leaves something for our kids. Highly recommend.' },
                { name: 'Mark K.', area: 'Old Meridian Village', stars: 5, text: 'Duke Energy\'s group plan was only $120K. Benjamin got us $1M of 20-year term for $47/month. I\'d been overpaying and underinsured for years before I called.' },
              ].map((t, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-gray-100">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: t.stars }).map((_, si) => <i key={si} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                  </div>
                  <p className="text-[14px] text-[#4A5568] leading-relaxed mb-3 italic">"{t.text}"</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
                      <i className="ri-user-line text-white text-sm"></i>
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-[#1A3A52]">{t.name}</p>
                      <p className="text-[11px] text-[#718096]">{t.area}, Carmel IN</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <h2 className="text-[28px] font-bold text-[#1A3A52] mb-2 text-center">Serving Every Carmel Neighborhood</h2>
          <p className="text-[15px] text-[#718096] mb-8 text-center max-w-[560px] mx-auto">No office visit needed. Phone and Zoom consultations available in all Carmel ZIP codes.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { name: 'Village of West Clay', detail: 'Luxury homes $600K–$1.2M — needs high-limit coverage' },
              { name: 'Clay Terrace Area', detail: 'Mixed-use, families near 126th & Keystone Pkwy' },
              { name: 'Carmel Arts District', detail: 'Young professionals, Monon Trail corridor' },
              { name: 'Old Meridian Village', detail: 'Townhomes & condos near 116th St' },
              { name: 'Hunters Run', detail: 'Established family neighborhood, $400K–$700K homes' },
              { name: 'Gramercy', detail: 'Newer townhomes near 126th & Range Line Rd' },
            ].map((n, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-[#F7F9FB] rounded-xl border border-gray-100">
                <i className="ri-map-pin-2-fill text-[#D97642] mt-0.5 text-lg flex-shrink-0"></i>
                <div>
                  <p className="font-bold text-[#1A3A52] text-[14px]">{n.name}</p>
                  <p className="text-[12px] text-[#718096] mt-0.5">{n.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Major Employers */}
      <section className="py-14 bg-[#F7F9FB]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <h2 className="text-[28px] font-bold text-[#1A3A52] mb-2 text-center">Carmel's Major Employers — And Why Their Group Plans Fall Short</h2>
          <p className="text-[15px] text-[#718096] mb-8 text-center max-w-[600px] mx-auto">Working at one of these companies? Your employer benefit is a starting point, not a plan. Here's the real gap.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {[
              { employer: 'CNO Financial Group', employees: '3,500+ in Carmel', gap: 'Even insurance professionals carry only 1–2× salary in group coverage. Personal policy fills the $800K–$1.5M gap.' },
              { employer: 'Duke Energy Indiana', employees: '1,200+ at Carmel HQ', gap: 'Group plan is portable only for 18 months. Personal coverage stays with you regardless of employment changes.' },
              { employer: 'ANGI / Angie\'s List', employees: '2,500+ in Carmel', gap: 'Tech salaries of $80K–$150K mean group plans provide $80K–$300K. Mortgage alone often exceeds that.' },
              { employer: 'Allied Solutions', employees: '800+ in Carmel', gap: 'Financial services professionals who help others with money but skip personal life insurance planning.' },
              { employer: 'Interactive Intelligence / Genesys', employees: '600+ in Carmel area', gap: 'Group coverage ends day one of a layoff — exactly when you\'re most financially vulnerable.' },
              { employer: 'Carmel Clay Schools', employees: '2,000+ teachers & staff', gap: 'PERF pension covers retirement but leaves $0 for survivors if you die before retirement age.' },
            ].map((e, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#1A3A52]/10 rounded-lg flex-shrink-0">
                  <i className="ri-building-line text-[#1A3A52] text-xl"></i>
                </div>
                <div>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                    <span className="font-bold text-[#1A3A52] text-[15px]">{e.employer}</span>
                    <span className="text-[11px] text-[#718096] bg-gray-100 px-2 py-0.5 rounded-full">{e.employees}</span>
                  </div>
                  <p className="text-[13px] text-[#718096] leading-relaxed">{e.gap}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-[13px] text-[#718096]">
            Work somewhere else? We cover <strong>all</strong> Carmel and Hamilton County residents. <a href="#carmel-quote-form" className="text-[#D97642] font-semibold hover:underline cursor-pointer">Get your free rate.</a>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-white">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8">
          <h2 className="text-[28px] font-bold text-[#1A3A52] mb-2 text-center">Frequently Asked Questions</h2>
          <p className="text-[15px] text-[#718096] mb-8 text-center">Life insurance questions specific to Carmel, Indiana families.</p>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F7F9FB] transition-colors cursor-pointer"
                >
                  <span className="font-semibold text-[#1A3A52] text-[15px] pr-4">{faq.q}</span>
                  <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-[#D97642] text-xl flex-shrink-0`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-[#4A5568] text-[14px] leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Hub Callout */}
      <section className="py-9 bg-[#0F2A3F]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white/5 border border-white/10 rounded-2xl px-7 py-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl flex-shrink-0">
                <i className="ri-book-open-line text-white text-[22px]"></i>
              </div>
              <div>
                <p className="text-[11px] text-[#D97642] font-bold uppercase tracking-widest mb-1">Indiana Life Insurance Learning Hub</p>
                <h3 className="text-[19px] font-bold text-white leading-tight mb-1">Not sure where to start with coverage?</h3>
                <p className="text-[14px] text-white/65">27 Indiana guides + 5 pillar posts + 7 city breakdowns — all in one place.</p>
              </div>
            </div>
            <Link to="/learning-hub" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap flex-shrink-0">
              <i className="ri-compass-3-line text-[17px]"></i>
              Explore the Learning Hub
              <i className="ri-arrow-right-line text-[15px]"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white">
        <div className="max-w-[700px] mx-auto px-4 sm:px-8 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-[#D97642]/20 rounded-full mx-auto mb-5">
            <i className="ri-shield-check-line text-[#D97642] text-2xl"></i>
          </div>
          <h2 className="text-[30px] font-bold mb-3">Protect Your Carmel Home & Family Today</h2>
          <p className="text-white/70 text-[16px] mb-8 max-w-[480px] mx-auto">One 15-minute call. 20+ carrier quotes. Real coverage for Carmel's real cost of living.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#carmel-quote-form" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[16px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-lg"></i>Get My Free Rate
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white text-[16px] font-bold rounded-xl border border-white/20 hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-line text-lg"></i>Call (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      {/* Indiana city cross-links */}
      <section className="py-12 bg-[#F7F9FB]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <h2 className="text-[20px] font-bold text-[#1A3A52] mb-1 text-center">Also Serving Indiana</h2>
          <p className="text-[14px] text-[#718096] mb-4 text-center">Dedicated city pages — same independent agent, same 20+ carriers.</p>
          <p className="text-center text-[12px] font-bold text-[#718096] uppercase tracking-widest mb-3">Hamilton County</p>
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {[
              { label: 'Life Insurance Fishers', href: '/life-insurance-fishers', sub: 'Roche, Navient, HSE Schools' },
              { label: 'Life Insurance Noblesville', href: '/life-insurance-noblesville', sub: 'Riverview Health, ADESA' },
              { label: 'Life Insurance Hamilton County', href: '/life-insurance-hamilton-county', sub: 'Full County Hub' },
            ].map((c) => (
              <Link key={c.label} to={c.href} className="block bg-white rounded-xl px-5 py-3 border border-gray-200 hover:border-[#D97642] transition-all text-center group cursor-pointer">
                <i className="ri-map-pin-2-fill text-[#D97642] text-lg mb-1 block"></i>
                <p className="font-bold text-[#1A3A52] text-[14px] group-hover:text-[#D97642] transition-colors whitespace-nowrap">{c.label}</p>
                <p className="text-[11px] text-[#718096] mt-0.5">{c.sub}</p>
              </Link>
            ))}
          </div>
          <p className="text-center text-[12px] font-bold text-[#718096] uppercase tracking-widest mb-3">Statewide Indiana</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'Life Insurance Indianapolis', href: '/life-insurance-indianapolis', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Fort Wayne', href: '/life-insurance-fort-wayne', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance South Bend', href: '/life-insurance-south-bend', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Evansville', href: '/life-insurance-evansville', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Indiana (Statewide)', href: '/life-insurance-indiana', icon: 'ri-map-2-line' },
              { label: 'No Medical Exam Options', href: '/no-medical-exam', icon: 'ri-stethoscope-line' },
            ].map(link => (
              <Link key={link.label} to={link.href} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-[13px] font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${link.icon} text-[#D97642] text-[14px]`}></i></div>
                {link.label}
              </Link>
            ))}
          </div>
          {/* Blog articles — topical authority */}
          <div className="mt-5 pt-5 border-t border-gray-100">
            <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-2">Learn More From Our Blog</p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { label: 'Wealth Building with Life Insurance in Indianapolis', href: '/blog/wealth-building-life-insurance-indianapolis' },
                { label: 'Indexed Universal Life Insurance in Indianapolis', href: '/blog/indexed-universal-life-indianapolis' },
                { label: 'Business Key Person Insurance in Indianapolis', href: '/blog/business-key-person-insurance-indianapolis' },
                { label: 'Life Insurance for the Self-Employed in Indiana', href: '/blog/life-insurance-self-employed-indiana' },
                { label: 'How Much Life Insurance Does an Indiana Family Need?', href: '/blog/how-much-life-insurance-indiana' },
              ].map(blog => (
                <Link key={blog.href} to={blog.href} className="flex items-center gap-2 px-4 py-2 bg-[#FDF6F0] border border-[#D97642]/20 rounded-lg text-[13px] text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-article-line text-[#D97642] text-[14px]"></i></div>
                  {blog.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
